import { useCallback } from 'react';
import { useAppKitProvider } from '@reown/appkit/react';
import { Provider, SendTransactionOptions } from '@reown/appkit-adapter-solana';
import * as anchor from '@coral-xyz/anchor';
import { useConnection } from '@solana/wallet-adapter-react';

export interface SvmSendTransactionOptions extends SendTransactionOptions {
  skipComputeBudget?: boolean;
  preSigned?: boolean; // Set to true if transaction already has partial signatures
}

export function useSvmSendTransaction() {
  const { walletProvider } = useAppKitProvider<Provider>('solana');
  const { connection } = useConnection();

  const svmSendTransaction = useCallback(
    async function (
      tx: anchor.web3.Transaction,
      options?: SvmSendTransactionOptions
    ) {
      // For pre-signed transactions, skip all modifications to avoid invalidating signatures
      if (!options?.preSigned) {
        // Only add compute budget instructions if not skipped
        if (!options?.skipComputeBudget) {
          const modifyComputeUnits =
            anchor.web3.ComputeBudgetProgram.setComputeUnitLimit({
              units: 2000000,
            });
          const addPriorityFee =
            anchor.web3.ComputeBudgetProgram.setComputeUnitPrice({
              microLamports: 9000,
            });
          tx.add(modifyComputeUnits).add(addPriorityFee);
        }

        const latestBlockhash1 = await connection.getLatestBlockhash('confirmed');

        tx.recentBlockhash = latestBlockhash1.blockhash;
        tx.feePayer = walletProvider.publicKey;
      }

      if (options && options.signers) {
        tx.sign(...options.signers);
      }

      const signature = await walletProvider.sendTransaction(
        tx as any,
        connection,
        {
          skipPreflight: false,
          maxRetries: 3,
          ...options,
        }
      );

      const { blockhash, lastValidBlockHeight } =
        await connection.getLatestBlockhash('confirmed');

      try {
        const res = await connection.confirmTransaction(
          {
            signature,
            blockhash,
            lastValidBlockHeight,
          },
          'confirmed'
        );
        if (res.value.err) {
          throw new Error(res.value.err.toString());
        }
        return { ...res.value, signature };
      } catch (error: any) {
        // Check if this is a "transaction already processed" error
        const errorMessage = error?.message || String(error);

        if (errorMessage.includes('already been processed') ||
            errorMessage.includes('This transaction has already been processed') ||
            errorMessage.includes('already processed')) {
          console.log(`ℹ️ Transaction was already processed, checking status...`);

          // Check the transaction status
          const status = await connection.getSignatureStatus(signature);

          if (status?.value?.confirmationStatus &&
              (status.value.confirmationStatus === 'confirmed' || status.value.confirmationStatus === 'finalized') &&
              !status.value.err) {
            console.log(`✅ Transaction was already confirmed successfully: ${signature}`);
            return { err: null, signature };
          }
        }

        // Re-throw the error if it's not a "transaction already processed" success case
        throw error;
      }
    },
    [connection, walletProvider]
  );

  return { svmSendTransaction };
}
